import { gql } from '@apollo/client'
import { refreshDeliveryNote } from 'client/graphQueries/deliverynotes'
import { refreshReceipt } from 'client/graphQueries/receipts'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'

import { restInvoiceClient } from './clients.configuration'

const commonFieldsFragment = `
  id
  issueDate
  dueDate
  referenceNumber
  title
  version
  customer {
    displayName
    customerType
    externalContactId
    address {
      country
    }
  }
`

const documentLinesFragment = `
  lines {
      ... on rowLine {type,date,description,productType,quantity,netTotal,taxTotal,grossTotal,vatPct,vatRateId,unit,unitPrice,totalDiscount}
    }
`

const slimSummaryFragment = `
  summary {
    localGrossTotal
    localCurrency
    localNetTotal
    localTaxTotal
  }`

const summaryFragment = `
summary {
  foreignGrossTotal
  localGrossTotal
  exchangeRate
  localCurrency
  foreignCurrency
  localNetTotal
  localTaxTotal
}
`
const transactionInfoFragment = `
transactionInfo {
  creditor {
    name
  }
  bookingDate
  transactionDate
  transactionAmount {
    currency
    amount
  }
  referenceNumber
  additionalInfo
}
`
const paidEventFragment = `
... on paidEventData {
  date
  accountId
  transactionId
  ${transactionInfoFragment}
  matchType
  ${summaryFragment}
}
`

const paidEventFragmentSlim = `
... on paidEventData {
  date
}
`

const createdEventFragment = `
... on createdEventData {
  revisionId
}
`

const modifiedEventFragment = `
... on modifiedEventData {
  newRevisionId,
  oldRevisionId
}
`

const modifiedEventExternalFragment = `
... on modifiedEventExternalData {
  archiveReportId
}
`

const fileCreatedEventFragment = `
... on fileCreatedEventData {
  revisionId
}
`

const emailSentEventDataFragment = `
... on emailSentEventData {
  recipient
}
... on reminderEmailSentEventData {
  recipient
}
`

const emailOpenedEventDataFragment = `
... on emailOpenedEventData {
  recipient
}
... on reminderEmailOpenedEventData {
  recipient
}
`

const emailClickedEventDataFragment = `
... on emailClickedEventData {
  recipient
}
... on reminderEmailClickedEventData {
  recipient
}
`

const emailComplainedEventDataFragment = `
... on emailComplainedEventData {
  recipient
}
... on reminderEmailComplainedEventData {
  recipient
}
`

const emailFailedEventDataFragment = `
... on emailFailedEventData {
  recipient
}
... on reminderEmailFailedEventData {
  recipient
}
`

const emailDeliveredEvenDataFragment = `
... on emailDeliveredEventData {
  recipient
}
... on reminderEmailDeliveredEventData {
  recipient
}
`

const emailSentFinancingEventDataFragment = `
... on emailSentFinancingEventData {
  partner
}
`

const orderCommonFragment = `
includeVat
name
orderType
`

const orderItemFragment = `
percentage
value
vat
dueDate
`

const orderInvoiceFragment = `
order {
  grossTotal
  netTotal
  taxTotal
  ${orderCommonFragment}
  itemIndex
  scheduleId
  items {
    ${orderItemFragment}
    referenceInvoiceNumber
  }
}`

const orderInvoiceSlimFragment = `
order {
  name
  orderType
  itemIndex
  items {
    referenceInvoiceNumber
  }
}`

const baswareSentEventDataFragment = `
... on baswareSentEventData {
  country
  baswareResponse {
    bumId
    sentOn
    __typename
  }
  __typename
}
`

const baswareNotificationEventDataFragment = `
... on baswareNotificationEventData {
  country
  baswareResponse {
    bumId
    sentOn
    notification {
      actionCode
      actionType
      description
      documentType
      responseCode
      responseType
      acknowledgementId
      zervantDescription
      __typename
    }
    __typename
  }
  __typename
}
`

const invoiceFields = `
{
  config {
    fields {
      template {
        value
      }
    }
  }
  ${commonFieldsFragment}
  ${documentLinesFragment}
  ${summaryFragment}
  recurringProfileId
  invoiceNumber
  paymentReferenceType
  externalId
  externalInvoiceId
  status {
    status
    secondaryStatus
    paidDate
    events {
      eventType
      createdAt
      processedAt
      eventData{
        ${paidEventFragment}
        ${createdEventFragment}
        ${modifiedEventFragment}
        ${modifiedEventExternalFragment}
        ${fileCreatedEventFragment}
        ${emailSentEventDataFragment}
        ${emailOpenedEventDataFragment}
        ${emailClickedEventDataFragment}
        ${emailComplainedEventDataFragment}
        ${emailFailedEventDataFragment}
        ${emailDeliveredEvenDataFragment}
        ${emailSentFinancingEventDataFragment}
        ${baswareSentEventDataFragment}
        ${baswareNotificationEventDataFragment}
      }
    }
  }
  ${orderInvoiceFragment}
}`

const invoiceFieldsSlim = `
{
  externalInvoiceId
  externalId
  invoiceNumber
  ${commonFieldsFragment}
  ${slimSummaryFragment}
  status {
    status
    secondaryStatus
    paidDate
  }
  ${orderInvoiceSlimFragment}
}
`

const creditNoteFieldsSlim = `
{
  ${commonFieldsFragment}
  ${slimSummaryFragment}
  externalCreditNoteId
  externalId
  creditNoteNumber
  status {
    status
    events {
      eventType
      createdAt
      eventData {
        ${baswareSentEventDataFragment}
        ${baswareNotificationEventDataFragment}
      }
    }
  }
}
`

const estimateFieldsSlim = `
{
  ${commonFieldsFragment}
  ${slimSummaryFragment}
  status {
    status
    events {
      eventType
      createdAt
      eventData {
        ${baswareSentEventDataFragment}
        ${baswareNotificationEventDataFragment}
      }
    }
  }
}
`

const creditNoteFields = `
{
  ${commonFieldsFragment}
  ${documentLinesFragment}
  ${summaryFragment}
  invoiceNumber
  externalId
  externalCreditNoteId
  creditNoteNumber
  status {
    status
    events {
      eventType
      createdAt
      eventData {
        ${paidEventFragment}
        ${createdEventFragment}
        ${emailSentEventDataFragment}
        ${emailOpenedEventDataFragment}
        ${emailClickedEventDataFragment}
        ${emailComplainedEventDataFragment}
        ${emailFailedEventDataFragment}
        ${emailDeliveredEvenDataFragment}
        ${modifiedEventExternalFragment}
        ${baswareSentEventDataFragment}
        ${baswareNotificationEventDataFragment}
      }
    }
  }
}
`

const draftFieldsSlim = `
{
  ${commonFieldsFragment}
  ${slimSummaryFragment}
}
`

const draftFields = `
{
  ${commonFieldsFragment}
  ${documentLinesFragment}
  ${summaryFragment}
}
`

export const GET_INVOICE_BY_ID_QUERY = gql`
  query invoice($id: Int) {
    invoice(id: $id) ${invoiceFields}
  }`

export const GET_CREDITNOTE_BY_ID_QUERY = gql`
  query invoice($id: Int) {
    creditNote(id: $id) ${creditNoteFields}
  }`

export const GET_DRAFT_INVOICE_BY_ID_QUERY = gql`
  query invoice($id: Int) {
    invoiceDraft(id: $id) ${draftFields}
  }`
const INVOICES_QUERY = `
  invoices ${invoiceFields}`

const DRAFT_INVOICES_QUERY = `
  invoiceDrafts ${draftFields}`

const CREDIT_NOTES_QUERY = `
  creditNotes ${creditNoteFields}`

export const GET_INVOICES_QUERY = gql`{
    invoices:
      ${INVOICES_QUERY}
  }`

export const GET_ALL_INVOICES_QUERY = gql`{
    invoices:
      invoices ${invoiceFieldsSlim}
    drafts:
      invoiceDrafts ${draftFieldsSlim}
    creditNotes:
      creditNotes ${creditNoteFieldsSlim}
  }`

export const GET_UNPAID_INVOICES_QUERY = gql`{
      invoices : invoices (status: open) {
        ${commonFieldsFragment}
        ${summaryFragment}
        invoiceNumber
        status {
          status
        }
      }
    }`

const GET_AVAILABLE_FIELD_DEFAULTS_QUERY = gql`
  query ($documentType: configTypes!) {
    availableFieldDefaults: availableFieldDefaults(type: $documentType) {
      title {
        de_AT
        de_CH
        de_DE
        en_AU
        en_GB
        fi_FI
        fr_BE
        fr_CH
        fr_FR
        nl_BE
        nl_NL
        sv_FI
        sv_SE
      }
    }
  }
`

export const getAllInvoices = () => {
  return restInvoiceClient.query({
    query: GET_ALL_INVOICES_QUERY,
  })
}

export const getUnpaidInvoicesData = () => {
  return restInvoiceClient.query({
    query: GET_UNPAID_INVOICES_QUERY,
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  })
}

export const getInvoice = (id: number) => {
  return restInvoiceClient.query({
    query: GET_INVOICE_BY_ID_QUERY,
    variables: { id },
  })
}

export const refreshDocument = (id: number, documentType: string) => {
  const options = { variables: { id }, fetchPolicy: 'network-only' }
  switch (documentType) {
    case 'draftInvoice':
      return restInvoiceClient.query({ query: GET_DRAFT_INVOICE_BY_ID_QUERY, ...options })
    case 'creditnote':
    case 'creditNote':
      return restInvoiceClient.query({ query: GET_CREDITNOTE_BY_ID_QUERY, ...options })
    case DocumentTypes.DELIVERYNOTE:
      return refreshDeliveryNote(id)
    case DocumentTypes.RECEIPT:
      return refreshReceipt(id)
    default:
      // invoice
      return restInvoiceClient.query({ query: GET_INVOICE_BY_ID_QUERY, ...options })
  }
}

export const getInvoices = () => {
  return restInvoiceClient.query({
    query: GET_INVOICES_QUERY,
  })
}

export const getDocumentTranslation = (documentType: DocumentTypes) => {
  return restInvoiceClient.query({
    query: GET_AVAILABLE_FIELD_DEFAULTS_QUERY,
    variables: { documentType },
  })
}
